/**
 * ReportDownloadDropdown — shared dropdown for downloading reports.
 *
 * Renders a button that toggles a dropdown panel with report type selection,
 * format selection, and a generate button. On generate, calls the provided
 * onGenerate callback and triggers a browser file download.
 *
 * By default the panel is absolutely positioned relative to the button.
 * Pass `panelContainerRef` to render the panel via a portal, matching
 * that element's width (useful inside drawers with overflow clipping).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Download, ChevronUp } from 'lucide-react';
import { Button } from 'equal-ds-ui';
import { downloadBlob } from '../../utils/download';

export interface ReportTypeOption {
  value: string;
  title: string;
  description: string;
}

export interface FormatOption {
  value: string;
  label: string;
}

export interface PostGenerateAction {
  label: string;
  onClick: () => void;
}

export interface ReportDownloadDropdownProps {
  buttonLabel?: string;
  buttonVariant?: 'primary' | 'secondary';
  reportTypes: ReportTypeOption[];
  defaultReportType?: string;
  formats: FormatOption[];
  defaultFormat?: string;
  onGenerate: (params: { reportType: string; format: string }) => Promise<Blob | null>;
  getFileName: (params: { reportType: string; format: string }) => string;
  onError?: (error: unknown) => void;
  disabled?: boolean;
  /** When onGenerate returns null, show this action button instead of closing the panel */
  postGenerateAction?: PostGenerateAction;
  /** Ref to the element whose width the dropdown panel should match (renders via portal) */
  panelContainerRef?: React.RefObject<HTMLElement>;
}

export const ReportDownloadDropdown: React.FC<ReportDownloadDropdownProps> = ({
  buttonLabel = 'Download Data',
  buttonVariant = 'primary',
  reportTypes,
  defaultReportType,
  formats,
  defaultFormat,
  onGenerate,
  getFileName,
  onError,
  disabled = false,
  postGenerateAction,
  panelContainerRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(defaultReportType || reportTypes[0]?.value || '');
  const [selectedFormat, setSelectedFormat] = useState(defaultFormat || formats[0]?.value || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [completedAction, setCompletedAction] = useState<PostGenerateAction | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  // Compute portal panel position based on button and container ref
  const computePortalPosition = useCallback(() => {
    if (!panelContainerRef?.current || !buttonRef.current) return;

    const containerRect = panelContainerRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();

    // Right-align to button, use container width but clamp to not overflow left
    const panelWidth = containerRect.width;
    const right = window.innerWidth - buttonRect.right;

    setPortalStyle({
      position: 'fixed',
      top: buttonRect.bottom + 4,
      right,
      width: panelWidth,
      zIndex: 9999,
      boxSizing: 'border-box',
    });
  }, [panelContainerRef]);

  useEffect(() => {
    if (isOpen && panelContainerRef) {
      computePortalPosition();
    }
  }, [isOpen, panelContainerRef, computePortalPosition]);

  // Close on outside click — check both the button container and the portal panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideButton = containerRef.current?.contains(target);
      const clickedInsidePanel = panelRef.current?.contains(target);
      if (!clickedInsideButton && !clickedInsidePanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Reset completed action when report type changes
  const handleReportTypeChange = (value: string) => {
    setSelectedReportType(value);
    setCompletedAction(null);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setCompletedAction(null);
    try {
      const blob = await onGenerate({ reportType: selectedReportType, format: selectedFormat });
      if (blob) {
        const fileName = getFileName({ reportType: selectedReportType, format: selectedFormat });
        downloadBlob(blob, fileName);
        setIsOpen(false);
      } else if (postGenerateAction) {
        setCompletedAction(postGenerateAction);
      } else {
        setIsOpen(false);
      }
    } catch (error) {
      console.error('[ReportDownloadDropdown] Generation failed:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const panelContent = (
    <>
      {/* Report Type Heading */}
      <p style={{ fontSize: '14px', color: '#666d78', fontWeight: 500, margin: '0 0 12px 0', letterSpacing: '0.28px', fontFamily: "'Helvetica Neue', sans-serif" }}>
        Choose the report you want to download
      </p>

      {/* Report Type Cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        {reportTypes.map((opt) => {
          const isSelected = selectedReportType === opt.value;
          return (
            <label
              key={opt.value}
              onClick={() => handleReportTypeChange(opt.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                flex: 1,
                minHeight: '60px',
                padding: '12px',
                backgroundColor: '#ffffff',
                border: isSelected ? '1px solid #c1e4fb' : '1px solid #e7edf0',
                borderBottom: isSelected ? '2px solid #c1e4fb' : '1px solid #e7edf0',
                borderRadius: '12px',
                transition: 'all 0.15s ease',
              }}
            >
              <input
                type="radio"
                name="reportType"
                checked={isSelected}
                onChange={() => handleReportTypeChange(opt.value)}
                style={{ width: '20px', height: '20px', flexShrink: 0, accentColor: '#0f3340' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '14px', fontWeight: isSelected ? 700 : 500, color: isSelected ? '#0f3340' : '#666d78', lineHeight: 'normal', letterSpacing: '0.28px', fontFamily: "'Helvetica Neue', sans-serif" }}>{opt.title}</span>
                <span style={{ fontSize: '12px', color: '#919cab', lineHeight: 'normal', letterSpacing: '0.24px', fontFamily: "'Helvetica Neue', sans-serif" }}>{opt.description}</span>
              </div>
            </label>
          );
        })}
      </div>

      {/* Format Selection */}
      {formats.length > 1 && (
        <>
          <p style={{ fontSize: '14px', color: '#666d78', fontWeight: 500, margin: '0 0 12px 0', letterSpacing: '0.28px', fontFamily: "'Helvetica Neue', sans-serif" }}>
            Choose your preferred format
          </p>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            {formats.map((fmt) => {
              const isSelected = selectedFormat === fmt.value;
              return (
                <label
                  key={fmt.value}
                  onClick={() => setSelectedFormat(fmt.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '12px',
                    width: '93.5px',
                    backgroundColor: '#ffffff',
                    border: isSelected ? '1px solid #d9f0ff' : '1px solid #e7edf0',
                    borderBottom: isSelected ? '2px solid #d9f0ff' : '1px solid #e7edf0',
                    borderRadius: '12px',
                    transition: 'all 0.15s ease',
                    boxSizing: 'border-box',
                  }}
                >
                  <input
                    type="radio"
                    name="format"
                    checked={isSelected}
                    onChange={() => setSelectedFormat(fmt.value)}
                    style={{ width: '20px', height: '20px', accentColor: '#0f3340', margin: 0, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: 500, color: isSelected ? '#0f3340' : '#666d78', letterSpacing: '0.28px', fontFamily: "'Helvetica Neue', sans-serif", whiteSpace: 'nowrap' }}>
                    {fmt.label}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}

      {/* Generate Report / Completed Action Button */}
      {completedAction ? (
        <button
          onClick={() => { completedAction.onClick(); setIsOpen(false); setCompletedAction(null); }}
          style={{
            width: '100%',
            height: '56px',
            backgroundColor: '#0f3340',
            border: '1px solid #021d27',
            borderBottom: '2px solid #021d27',
            borderRadius: '8px',
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 500,
            letterSpacing: '0.4px',
            fontFamily: "'Helvetica Neue', sans-serif",
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px',
          }}
        >
          {completedAction.label}
        </button>
      ) : (
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          style={{
            width: '100%',
            height: '56px',
            backgroundColor: '#0f3340',
            border: '1px solid #021d27',
            borderBottom: '2px solid #021d27',
            borderRadius: '8px',
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 500,
            letterSpacing: '0.4px',
            fontFamily: "'Helvetica Neue', sans-serif",
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            opacity: isGenerating ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '12px',
          }}
        >
          {isGenerating ? (
            <>
              <span style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#ffffff',
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'rdd-spin 0.7s linear infinite',
                flexShrink: 0,
              }} />
              GENERATING...
            </>
          ) : 'GENERATE REPORT'}
        </button>
      )}
    </>
  );

  const panelStyles: React.CSSProperties = {
    backgroundColor: '#ffffff',
    border: '1px solid #d9f0ff',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
    padding: '12px',
    boxSizing: 'border-box',
  };

  // Portal panel — rendered into document.body, escapes overflow clipping
  const portalPanel = (
    <div ref={panelRef} style={{ ...portalStyle, ...panelStyles }}>
      {panelContent}
    </div>
  );

  // Inline absolute panel — positioned relative to button
  const absolutePanel = (
    <div ref={panelRef} style={{
      position: 'absolute',
      top: '100%',
      right: 0,
      width: '660px',
      marginTop: '4px',
      zIndex: 40,
      ...panelStyles,
    }}>
      {panelContent}
    </div>
  );

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <style>{`@keyframes rdd-spin { to { transform: rotate(360deg); } }`}</style>
      <div ref={buttonRef}>
        <Button
          variant={buttonVariant}
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled || isGenerating}
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <Download className="w-4 h-4" />
          {isGenerating ? 'Generating...' : buttonLabel}
          <ChevronUp className={`w-4 h-4 transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`} />
        </Button>
      </div>

      {isOpen && (
        panelContainerRef
          ? ReactDOM.createPortal(portalPanel, document.body)
          : absolutePanel
      )}
    </div>
  );
};
